import { BaseModel } from '../modules/_base/base.model'
import { useAxios } from '../modules/axios/axiosModule'
import { useApollo } from '../modules/apollo/apolloModule'
import { useConfig } from '../modules/config/configModule'
import authStoreGetters from '../modules/authentication/store/getters'

/**
 * Replaces `BaseCrudService` — the `extends` half of every domain service — with a composable the
 * service spreads into its own return, as AuthorBase does.
 *
 * The abstract base was doing two unrelated jobs: handing out four shared helpers, and forcing every
 * subclass to declare `create`/`get`/`getById`/`update`/`delete`/`cleanFiles` whether or not it had
 * them (UserService has three `throw new Error('Unauthorised')` stubs purely to satisfy it). Only
 * the helpers are worth keeping, so only they move here.
 *
 * Deliberately does *not* provide `globalLoader`. It is chained
 * (`globalLoader(false).getUsersToMimic(...)`), so it has to return the service's own bag — which
 * only the service can name. Each service declares it in four lines against its own object.
 */

export interface BaseCrudOptions<T extends BaseModel> {
  /**
   * Gate on `addPictureFactory`; only bookService overrides it (page-count/cover rules).
   */
  validateAddPictureFactory?: (files: any[], model?: T) => Promise<boolean>
}

export interface PictureUploadFactory {
  url: string
  withCredentials: boolean
  headers: { name: string, value: string }[]
  formFields: { name: string, value: number }[] | undefined
}

export function useBaseCrud<T extends BaseModel> (
  apiControllerPath: string,
  options: BaseCrudOptions<T> = {}
) {
  const getApiControllerUrl = (path: string) => {
    return `${useConfig().config().axios.url}${apiControllerPath}/${path}`
  }

  const validateAddPictureFactory = (files: any[], model?: T): Promise<boolean> => {
    if (options.validateAddPictureFactory) {
      return options.validateAddPictureFactory(files, model)
    }

    return Promise.resolve(true)
  }

  const cleanFiles = async (fileName?: string): Promise<string | undefined> => {
    if (!fileName) {
      return void 0
    }

    const res = await useAxios().axios().post(getApiControllerUrl('clean-files'), {
      fileType: '',
      fileName
    })

    return res.data
  }

  /**
   * Builds the descriptor QUploader needs. The token pair is read at call time so an upload started
   * after a token refresh carries the new credentials.
   */
  const addPictureFactory = (files: any[], model?: T): Promise<PictureUploadFactory> => {
    return validateAddPictureFactory(files, model).then((valid: boolean) => {
      if (!valid) {
        return Promise.reject()
      }

      let formFields: { name: string, value: number }[] | undefined = void 0
      if (model) {
        formFields = [
          {
            name: 'id',
            value: model.id === void 0 ? -1 : model.id
          }
        ]
      }

      return {
        url: getApiControllerUrl('add-picture'),
        withCredentials: true,
        headers: [
          { name: 'Authorization', value: `Bearer ${authStoreGetters.authToken}` },
          { name: 'CSRF-Token', value: authStoreGetters.csrfToken }
        ],
        formFields
      }
    })
  }

  /**
   * The apollo loader flag, for services that want to set it without chaining.
   */
  const setGlobalLoader = (enable: boolean) => {
    useApollo().globalLoader(enable)
  }

  return {
    apiControllerPath,
    getApiControllerUrl,
    validateAddPictureFactory,
    cleanFiles,
    addPictureFactory,
    setGlobalLoader
  }
}
